import React, { useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { 
  Email, 
  AccessTime, 
  BugReport, 
  LightbulbOutlined,
  ExpandMore,
  Info,
  HelpOutline
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export const ContactPage: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const contactCards = [
    {
      icon: <Email />,
      title: t('contact_email_support'),
      subtitle: t('contact_email_subtitle'),
      actionText: 'support@nagriksahayak.in',
      action: () => window.location.href = 'mailto:support@nagriksahayak.in'
    },
    {
      icon: <BugReport />,
      title: t('contact_bug_report'),
      subtitle: t('contact_bug_subtitle'),
      actionText: t('contact_bug_action'),
      action: () => window.location.href = 'mailto:support@nagriksahayak.in?subject=Bug Report - Nagrik Sahayak'
    },
    {
      icon: <LightbulbOutlined />,
      title: t('contact_feature_request'),
      subtitle: t('contact_feature_subtitle'),
      actionText: t('contact_feature_action'),
      action: () => window.location.href = 'mailto:support@nagriksahayak.in?subject=Feature Request - Nagrik Sahayak'
    }
  ];

  const faqs = [
    {
      question: t('contact_faq_q1'),
      answer: t('contact_faq_a1')
    },
    {
      question: t('contact_faq_q2'),
      answer: t('contact_faq_a2')
    },
    {
      question: t('contact_faq_q3'),
      answer: t('contact_faq_a3')
    },
    {
      question: t('contact_faq_q4'),
      answer: t('contact_faq_a4')
    }
  ];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" color="#673AB7" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
        {t('contact_page_title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
        {t('contact_page_subtitle')}
      </Typography>

      {/* Contact Cards */}
      {contactCards.map((card, index) => (
        <Card 
          key={index}
          elevation={2}
          sx={{ 
            mb: 2,
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 4
            }
          }}
          onClick={card.action}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{
                  bgcolor: 'rgba(103,58,183,0.1)',
                  color: '#673AB7',
                  width: 56,
                  height: 56,
                  mr: 2
                }}
              >
                {card.icon}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  {card.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {card.subtitle}
                </Typography>
                <Typography variant="body2" color="primary" fontWeight={500}>
                  {card.actionText}
                </Typography>
              </Box>
              <Box sx={{ color: '#BDBDBD', fontSize: 20 }}>→</Box>
            </Box>
          </CardContent>
        </Card>
      ))}

      {/* Response Time Info */}
      <Card elevation={1} sx={{ mb: 4, bgcolor: '#E8F5E9' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccessTime sx={{ color: '#43A047', fontSize: 32, mr: 2 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {t('contact_response_time')}
              </Typography>
              <Typography variant="body1">
                {t('contact_response_text')}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* FAQs */}
      <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
        {t('contact_faq_title')}
      </Typography>

      {faqs.map((faq, index) => (
        <Accordion key={index} elevation={1} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <HelpOutline sx={{ color: '#673AB7', mr: 1 }} />
              <Typography fontWeight={600}>{faq.question}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography color="text.secondary">
              {faq.answer}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Container>
  );
};
